import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DeleteDialog } from "components";
import { UserAvatar } from "components/Atoms/Avatar";
import Panel from "components/panels/panel";
import React from "react";
import { useCustomQuery, useManualPagination } from "utilities/hookutils";
import { routes } from "utilities/routing/generatedRoutes";
import CreateIcalFeed from "./create";
import PermissionCheckModal from "components/modals/PermissionCheckModal";
import { useShortcutPanel } from "components/Shortcuts/hooks";
import { ButtonSecondary } from "components/Atoms/Button";
import { PhosphorTrashButton } from "components/Atoms/PhosphorIconButton";
import SubLabel from "components/Atoms/Sublabel";
import { PhosphorPencilAnchor } from "components/Atoms/PhosphorIconAnchor";

interface Props {
  artistId: number
  config: server.dto.LinkedICalendarFeedPanelConfig
}

const ICalFeedList = (props: Props) => {
  const perPage = 10;
  const icalFeedsCacheKey = "icalFeeds";

  const [isDeletingFeed, setIsDeletingFeed] = React.useState<server.dto.ICalendarFeed>(undefined);
  const [isCreatingFeed, setIsCreatingFeed] = React.useState<boolean>(false);

  const [instruction, setInstruction] = React.useState<server.dto.ListICalendarFeedInstruction>({
    artistId: props.artistId,
    page: 1,
    perPage
  });

  const feedsApi = useCustomQuery({
    queryKey: [icalFeedsCacheKey, instruction],
    queryFn: async () => window.CoreApi.ICalendarFeed.List(instruction).then(r => r.data),
    onSuccess: res => {
      pagination.setPagination(res.pagination);
    }
  });

  const deleteFeedApi = useMutation({
    retry: false,
    mutationFn: window.CoreApi.ICalendarFeed.Delete,
    onSuccess: () => {
      queryClient.invalidateQueries([icalFeedsCacheKey]);
      setIsDeletingFeed(undefined);
    }
  });

  const queryClient = useQueryClient();

  const pagination = useManualPagination(1, perPage, (pagination) => {
    setInstruction(prevInstr => ({
      ...prevInstr,
      page: pagination.page,
      perPage: pagination.perPage
    }));
  });

  const onDeleteClick = (feed: server.dto.ICalendarFeed) => {
    setIsDeletingFeed(feed);
  };

  const onDelete = async () => {
    return deleteFeedApi.mutateAsync(isDeletingFeed.iCalendarFeedId);
  };

  const onCreated = () => {
    setIsCreatingFeed(false);
    queryClient.invalidateQueries([icalFeedsCacheKey]);
  };

  const { refToScrollTo } = useShortcutPanel({
    type: "icalFeed",
    label: props.config.headerLabel,
    totalItems: pagination.total,
    onAddClicked: props.config.canAdd ? () => { setIsCreatingFeed(true); } : undefined

  });

  const canModify = site.permissions.canConfigureSystemSettings;

  if (feedsApi.data == null) {
    return <Panel
      panelRef={refToScrollTo}
      title={props.config.headerLabel}
      articleId={serverReference.supportArticleIds.iCalendarFeeds}
      isLoading
    ></Panel>;
  }

  return <>
    <Panel
      panelClassName="s1_related-feature-panel"
      panelRef={refToScrollTo}
      title={props.config.headerLabel}
      pagination={pagination.Component}
      hasData={feedsApi.data.data.length > 0}
      articleId={serverReference.supportArticleIds.iCalendarFeeds}
      button={props.config.canAdd && <ButtonSecondary onClick={() => { setIsCreatingFeed(true); }} label={RESX.GeneralLabels.resxNew} />}
    >
      <div className="list-with-gaps line-layout">
        <div className="list-with-gaps-body">
          {feedsApi.data.data.map(feed => (<div key={feed.iCalendarFeedId}>
            <div><UserAvatar user={feed.owner.user} /></div>
            <div className="grow1">
              <div>{feed.owner.user.fullName}</div>
              <SubLabel>{feed.owner.environment === "internal" ? RESX.UserType.Internal : RESX.UserType.External}</SubLabel>
            </div>
            <div style={{ flex: "0 0 60%" }}>
              {canModify && <div>
                <a href={routes.systemSetting_ICalendarFeed_Detail(feed.iCalendarFeedId).absolute}>
                  {feed.title}
                </a>
              </div>}
              {!canModify && <div>{feed.title}</div>}

              {(feed.template) && <SubLabel>{RESX.Template.ICalendarTemplate}: {feed.template.title}</SubLabel>}
            </div>

            {canModify && <PhosphorTrashButton
              layout="in-square"
              onClick={() => onDeleteClick(feed)} />}

            {canModify && <PhosphorPencilAnchor
              layout="in-square"
              href={routes.systemSetting_ICalendarFeed_Detail(feed.iCalendarFeedId).absolute}
            />}
          </div>))}
        </div>
      </div >
    </Panel >
    <DeleteDialog
      isOpen={isDeletingFeed != null}
      message={RESX.GeneralWarnings.DeleteSure}
      handleClose={() => { setIsDeletingFeed(undefined); }}
      onDelete={onDelete}
    />
    <PermissionCheckModal
      isOpen={isCreatingFeed}
      handleClose={() => { setIsCreatingFeed(false); }}
      title={RESX.Feed.iCalendarFeed}
      width="tiny"
      permission={{
        permissionName: RESX.GeneralLabels.SystemSettings,
        hasPermission: canModify
      }}
    >
      <CreateIcalFeed
        excludedUsers={feedsApi.data.data.map(feed => feed.owner.userToClientId)}
        onCreated={onCreated}
        artistId={props.artistId}
      />
    </PermissionCheckModal>
  </>;
};

export default ICalFeedList;